import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, Archive, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CircularProgress } from '@mui/material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import dayjs from 'dayjs';
import { useNotifications } from '../contexts/NotificationContext';

const Notification = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('Latest');
  const [currentPage, setCurrentPage] = useState(1);
  const [archivingId, setArchivingId] = useState(null);
  const isMobile = useMediaQuery('(max-width:768px)');
  const { getAllNotifications, getArchivedNotifications, archiveNotification, loading } = useNotifications();
  
  // Get notifications based on active tab from context
  const allTabNotifications = activeTab === 'Archived' 
    ? getArchivedNotifications() 
    : getAllNotifications();
  
  // Pagination logic - 10 notifications per page
  const notificationsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(allTabNotifications.length / notificationsPerPage));
  const startIndex = (currentPage - 1) * notificationsPerPage;
  const endIndex = startIndex + notificationsPerPage;
  const notifications = allTabNotifications.slice(startIndex, endIndex);
  
  // Reset to page 1 when changing tabs
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);
  
  // Handle archive button click
  const handleArchive = async (notificationId) => {
    setArchivingId(notificationId);
    await archiveNotification(notificationId);
    setArchivingId(null);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      {/* Main Content */}
      <div className={`flex-1 bg-[#FCF8F3] ${isMobile ? 'pt-[80px]' : 'pt-[100px]'} ${isMobile ? 'px-0' : 'px-30'}`}>
        <div className={`bg-[#FCF8F3] ${isMobile ? 'mx-0' : 'mx-auto mt-6'}`}>
          {/* Header */}
          <div className="border-b border-gray-200">
            <div className={`flex items-center ${isMobile ? 'justify-between p-4' : 'justify-between p-6'}`}>
              <div className="flex items-center gap-4 flex-1">
                <h2 className={`font-bold ${isMobile ? 'text-2xl' : 'text-5xl'}`}>{t('Notification')}</h2>
              </div>
            </div>

            {/* Filter Tabs and Pagination */}
            <div className={`flex justify-between items-center ${isMobile ? 'px-4 pb-3' : 'px-6 pb-4'}`}>
              {/* Filter Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('Latest')}
                  className={`px-4 py-1 rounded-full text-sm font-medium transition-colors cursor-pointer border ${activeTab === 'Latest'
                      ? 'bg-gray-200 text-black'
                      : 'bg-white text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {t('Latest')}
                </button>
                <button
                  onClick={() => setActiveTab('Archived')}
                  className={`px-4 py-1 rounded-full text-sm font-medium transition-colors cursor-pointer border ${activeTab === 'Archived'
                      ? 'bg-gray-200 text-black'
                      : 'bg-white text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {t('Archived')}
                </button>
              </div>

              {/* Pagination on the right */}
              <div className={`flex items-center ${isMobile ? 'gap-1.5' : 'gap-2'}`}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className={`${isMobile ? 'px-2 py-1' : 'px-3 py-1.5'} border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed`}
                >
                  <ChevronLeft className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} strokeWidth={1.5} />
                </button>
                <div className="relative">
                  <select
                    value={currentPage}
                    onChange={(e) => setCurrentPage(Number(e.target.value))}
                    className={`appearance-none ${isMobile ? 'px-3 py-1 pr-7 text-sm' : 'px-4 py-1.5 pr-8'} border border-gray-300 rounded-lg font-medium hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-200`}
                  >
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <option key={page} value={page}>
                        {page}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className={`absolute ${isMobile ? 'right-1.5' : 'right-2'} top-1/2 -translate-y-1/2 h-3 w-3 pointer-events-none text-gray-600`} strokeWidth={2} />
                </div>
                <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-400 whitespace-nowrap`}>
                  / {totalPages} {t('Pages')}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className={`${isMobile ? 'px-2 py-1' : 'px-3 py-1.5'} border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed`}
                >
                  <ChevronRight className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className={isMobile ? 'p-4' : 'p-6'}>
            {loading ? (
              <div className="text-center py-12 text-gray-400">
                <p>{t('Loading')}</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p>{t('NoNotificationsYet')}</p>
              </div>
            ) : (
              <div className="space-y-0">
                {notifications.map((notification, index) => (
                  <div
                    key={notification._id || index}
                    className={`flex items-start gap-3 py-4 border-b border-gray-200 last:border-0 }`}
                  >
                    <div className="flex items-start gap-3 flex-1 w-full">
                      {!notification.isArchived && (
                        <div className="w-2 h-2 rounded-full bg-yellow-400 mt-2 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-base flex items-center gap-2">
                          {notification.type === 'deworming' ? t('DewormingReminder') : notification.type === 'nearbyLostPets' ? t('NearbyLostPets') : 'Notification'}
                        {notification.type === 'nearbyLostPets' && notification.nearbyPetLost && (
                          <Search
                            size={18}
                            className="text-yellow-500 hover:text-yellow-600 cursor-pointer"
                            onClick={() => {
                              window.location.href = notification.nearbyPetLost;
                            }}
                          />
                        )}
                        </h4>
                        
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.type === 'deworming' ? `${notification.petName} - ${dayjs(notification.nextEventDate).format('DD/MM/YYYY')}` : notification.type === 'nearbyLostPets' ? t('ClickIcon') : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm text-gray-500 whitespace-nowrap flex-shrink-0 ${isMobile ? 'text-xs' : ''}`}>
                        {dayjs(notification.createdAt).format('DD/MM/YYYY')}
                      </span>
                      {!notification.isArchived && (
                        <button
                          onClick={() => handleArchive(notification._id)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title="Archive notification"
                          disabled={archivingId === notification._id}
                        >
                          {archivingId === notification._id ? (
                            <CircularProgress size={16} thickness={4} sx={{ color: '#9CA3AF' }} />
                          ) : (
                            <Archive size={16} className="text-gray-500 hover:text-gray-700 cursor-pointer" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Notification;
